import React from 'react';
import {
  Button,
  Tag,
  TagSkeleton,
  Link,
  OverflowMenu,
  OverflowMenuItem,
  SkeletonText,
  InlineLoading,
  Modal
} from 'carbon-components-react';
import { isIOS } from 'react-device-detect';
import {
  WarningHex16,
  OverflowMenuHorizontal32,
  ArrowRight16,
  ArrowLeft16,
  ArrowLeft24,
  Add16,
  Notification16,
  CopyFile16,
  Checkmark16,
  Close20
} from '@carbon/icons-react';
import { SCREEN_SIZE } from 'types';
import { MapView } from '../mapView';
import Utils from 'utilities/helper.util';

type Props = {
  enquiryDetail: any;
  headerTitle: string;
  showBackNavigation: boolean;
  screenSize: string;
  showGetPlansButton: boolean;
  handleGetPlansClick: Function;
  handleBackButtonClick: any;
  isLoading: boolean;
  isModal: boolean;
  onClickClose?: any;
  responsesExpired: boolean;
  showResponsesExpiredModal: boolean;
  shareId: string;
  bodyDivRef?: any;
  setLualExtent: Function;
  onMiniMapLoaded?: Function;
  isUpdatingEnquiry: any;
  isFromSwxLocate: boolean;
  showMapModal: boolean;
  mapModalLoaded: boolean;
  showDetails: boolean;
  showAutoRenewOption: boolean;
  autoRenewingStatus: string;
  isEnquiryCreatedToday: boolean;
  showMoreDetails: boolean;

  handleModalState: () => void;
  setMapModalLoaded: (loaded: boolean) => void;
  handleDuplicateEnquiry: (enquiryId: string) => void;
  toggleDetailsPanel: () => void;
  toggleNotificationOptions: (val: boolean) => void;
  offEnquiryAutoRenew: () => void;
  toggleShowMoreDetailsSection: () => void;
  splitArrayToNextLine: (data: any) => React.ReactElement[];
  splitToNextLine: (data: string) => React.ReactElement[] | string;
  formatEnquirerDetail: (enquirer: any) => React.ReactElement;
};

const DesktopView: React.FC<Props> = ({
  enquiryDetail,
  headerTitle,
  showBackNavigation,
  screenSize,
  showGetPlansButton,
  handleGetPlansClick,
  handleBackButtonClick,
  isLoading,
  isModal,
  onClickClose,
  responsesExpired,
  showResponsesExpiredModal,
  // shareId,
  // bodyDivRef,
  setLualExtent,
  isUpdatingEnquiry,
  isFromSwxLocate,
  showMapModal,
  mapModalLoaded,
  showDetails,
  showAutoRenewOption,
  autoRenewingStatus,
  isEnquiryCreatedToday,
  showMoreDetails,

  handleModalState,
  setMapModalLoaded,
  handleDuplicateEnquiry,
  toggleDetailsPanel,
  toggleNotificationOptions,
  offEnquiryAutoRenew,
  toggleShowMoreDetailsSection,
  splitArrayToNextLine,
  splitToNextLine,
  formatEnquirerDetail
}) => {
  const overFlowMenuOption1 = (
    <div className="option-wrapper">
      <div className="option-text">Response notification options</div>
      <div className="option-icon">
        <Notification16 />
      </div>
    </div>
  );
  const overFlowMenuOption2 = (
    <div className="option-wrapper">
      <div className="option-text">Duplicate enquiry</div>
      <div className="option-icon">
        <CopyFile16 />
      </div>
    </div>
  );
  const overFlowMenuOption3 = (
    <div className="option-wrapper">
      <div className="option-text">
        {isUpdatingEnquiry
          ? 'Saving changes...'
          : `Auto-renewing (${autoRenewingStatus})`}
      </div>
      <div className="option-icon">
        {autoRenewingStatus === 'On' && <Checkmark16 />}
      </div>
    </div>
  );
  const overFlowMenuOffset =
    screenSize === SCREEN_SIZE.SM
      ? { top: 28, left: -128 }
      : { top: -30, left: -394 };

  const overFlowMenuOffsetModal =
    screenSize === SCREEN_SIZE.SM
      ? { top: 78, left: -128 }
      : { top: 6, left: -139 };

  return (
    <div
      className={`enquiryInformation ${isFromSwxLocate ? 'locate-theme' : ''}`}
    >
      {isModal && (
        <div className="close-button-wrapper">
          <div className="close-button-container" onClick={onClickClose}>
            <Close20 />
          </div>
        </div>
      )}
      {/* hide here */}
      {!isFromSwxLocate && (
        <div
          className={`enquiryInformation--header ${
            responsesExpired || showResponsesExpiredModal
              ? 'enquiryInformation--header-expired'
              : ''
          }`}
        >
          {(!responsesExpired || !showResponsesExpiredModal) && (
            <>
              <div className="enquiryInformation--header-left">
                {showBackNavigation && screenSize !== SCREEN_SIZE.SM && (
                  <>
                    <div
                      className="enquiryInformation--header-left-back"
                      onClick={handleBackButtonClick}
                    >
                      <div className="enquiryInformation--header-left-back-icon">
                        <ArrowLeft16 />
                      </div>
                      <div className="enquiryInformation--header-left-back-text">
                        Overview
                      </div>
                    </div>
                  </>
                )}
                {showBackNavigation && screenSize === SCREEN_SIZE.SM && (
                  <div onClick={handleBackButtonClick}>
                    <ArrowLeft24 />
                  </div>
                )}
              </div>
              <div className="enquiryInformation--header-middle">
                <div
                  className={`enquiryInformation--header-middle-title ${
                    isModal ? 'title-modal' : ''
                  }`}
                >
                  <div className="enquiryInformation--header-middle-title-container">
                    <div className="enquiryInformation--header-middle-title-jobId">
                      {!isLoading && enquiryDetail?.externalId ? (
                        <Tag className="tag-jobId">
                          Job {enquiryDetail?.externalId}
                        </Tag>
                      ) : (
                        <TagSkeleton />
                      )}
                    </div>
                    <div className="enquiryInformation--header-middle-title-sandbox">
                      {enquiryDetail?.isSandboxTest && (
                        <Tag className="tag-sandbox">Sandbox</Tag>
                      )}
                    </div>
                  </div>
                  <div className="enquiryInformation--header-middle-title-wording">
                    {headerTitle}
                  </div>
                </div>
                <div className="enquiryInformation--header-middle-emergency">
                  <div className="enquiryInformation--header-middle-emergency-link">
                    <Link
                      href="https://www.byda.com.au/what-to-do-in-an-emergency"
                      target="_blank"
                    >
                      What to do in an emergency
                    </Link>
                  </div>
                  <div className="enquiryInformation--header-middle-emergency-icon">
                    <WarningHex16 />
                  </div>
                </div>
              </div>
              <div className="enquiryInformation--header-right">
                <div
                  className="enquiryInformation--header-right-content"
                  data-floating-menu-container
                >
                  <OverflowMenu
                    size="xl"
                    flipped
                    ariaLabel={'Options'}
                    iconDescription={'Options'}
                    renderIcon={OverflowMenuHorizontal32}
                    className="enquiryInformation--header-right-content-overflowmenu"
                    menuOffsetFlip={
                      isModal ? overFlowMenuOffsetModal : overFlowMenuOffset
                    }
                    menuOptionsClass="enquiryInformation--header-right-content-overflowmenu-options"
                  >
                    <OverflowMenuItem
                      itemText={overFlowMenuOption1}
                      onClick={() => toggleNotificationOptions(true)}
                      className="enquiryInformation--header-right-content-overflowmenu-options-item"
                      hasDivider
                    />
                    <OverflowMenuItem
                      itemText={overFlowMenuOption2}
                      className="enquiryInformation--header-right-content-overflowmenu-options-item"
                      hasDivider
                      onClick={() => handleDuplicateEnquiry(enquiryDetail?.id)}
                    />
                    {showAutoRenewOption && (
                      <OverflowMenuItem
                        itemText={overFlowMenuOption3}
                        className="enquiryInformation--header-right-content-overflowmenu-options-item"
                        hasDivider
                        onClick={offEnquiryAutoRenew}
                        disabled={
                          isUpdatingEnquiry ||
                          autoRenewingStatus === 'Completed'
                        }
                      />
                    )}
                  </OverflowMenu>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {/* hide here */}
      <div className="enquiryInformation--details">
        {(!responsesExpired || !showResponsesExpiredModal) && (
          <>
            <div
              className={`enquiryInformation--details-show ${
                showDetails ? 'visible' : ' hide'
              }`}
            >
              <div className="enquiryInformation--details-show-info">
                <div className="enquiryInformation--details-show-info-location">
                  <div
                    className="enquiryInformation--details-show-info-location-map"
                    onClick={handleModalState}
                  >
                    <MapView
                      baseMapTheme={'light'}
                      enquiryShape={enquiryDetail?.shape}
                      setLualExtent={setLualExtent}
                    />
                  </div>
                  <div className="enquiryInformation--details-show-info-location-address">
                    <div className="text-styling-header">JOB SITE</div>
                    <div className="text-styling-body">
                      {!isLoading && enquiryDetail?.Address ? (
                        Utils.getFullAddress(enquiryDetail.Address)
                      ) : (
                        <SkeletonText />
                      )}
                    </div>
                  </div>
                </div>
                <div className="enquiryInformation--details-show-info-job-dates">
                  <div className="text-styling-header semi-bold">JOB DATES</div>
                  <div className="text-styling-body">
                    {!isLoading && enquiryDetail ? (
                      <div>
                        {Utils.toLocalDateFormat(enquiryDetail?.digStartAt)}{' '}
                        {screenSize !== SCREEN_SIZE.SM ? <br /> : ''}
                        {isIOS ? (
                          <span className="arrow-symbol">&#8594;</span>
                        ) : screenSize === SCREEN_SIZE.SM ? (
                          <span className="arrow-symbol">&#8594;</span>
                        ) : (
                          '⤷'
                        )}{' '}
                        {Utils.toLocalDateFormat(enquiryDetail?.digEndAt)}
                      </div>
                    ) : (
                      <SkeletonText />
                    )}
                  </div>
                </div>
                <div className="enquiryInformation--details-show-info-enquiry-date">
                  <div className="text-styling-header">ENQUIRY DATE</div>
                  <div className="text-styling-body">
                    {!isLoading && enquiryDetail?.createdAt ? (
                      <div>
                        {isEnquiryCreatedToday ? (
                          <>
                            Today
                            {screenSize !== SCREEN_SIZE.SM ? <br /> : ' '}
                            {Utils.toLocalTimeFormat(enquiryDetail.createdAt)}
                          </>
                        ) : (
                          <>
                            {Utils.toLocalDateFormat(enquiryDetail.createdAt)}
                            {screenSize !== SCREEN_SIZE.SM ? <br /> : ' '}
                            {Utils.toLocalTimeFormat(enquiryDetail.createdAt)}
                          </>
                        )}
                      </div>
                    ) : (
                      <SkeletonText />
                    )}
                  </div>
                </div>
                <div className="enquiryInformation--details-show-info-reference">
                  <div className="text-styling-header">YOUR REFERENCE</div>
                  <div className="text-styling-body">
                    {(!isLoading && enquiryDetail?.userReference) || (
                      <SkeletonText />
                    )}
                  </div>
                </div>
              </div>
              <div className="enquiryInformation--details-show-action">
                {showGetPlansButton && (
                  <div className="enquiryInformation--details-show-action-get-plans-button">
                    <Button
                      renderIcon={ArrowRight16}
                      onClick={handleGetPlansClick}
                    >
                      View responses
                    </Button>
                  </div>
                )}

                {screenSize !== SCREEN_SIZE.SM &&
                  showMoreDetails &&
                  !showGetPlansButton &&
                  !isLoading && (
                    <Button
                      onClick={toggleShowMoreDetailsSection}
                      kind={'ghost'}
                      className={'toggle-more-details'}
                    >
                      Less detail
                    </Button>
                  )}

                {!showMoreDetails && !showGetPlansButton && !isLoading && (
                  <Button
                    onClick={toggleShowMoreDetailsSection}
                    kind={'tertiary'}
                    className={'toggle-more-details'}
                  >
                    More details ...
                  </Button>
                )}
              </div>
            </div>
            <div
              className={`enquiryInformation--details-more-details ${
                showDetails && showMoreDetails ? 'visible' : 'hide'
              }`}
            >
              <div className="enquiryInformation--details-more-details-info">
                <div className="enquiryInformation--details-more-details-info-activity">
                  <div className="text-styling-header semi-bold">ACTIVITY</div>
                  <div className="text-styling-body">
                    {!isLoading && enquiryDetail?.activityTypes ? (
                      splitArrayToNextLine(enquiryDetail.activityTypes)
                    ) : (
                      <SkeletonText />
                    )}
                  </div>
                </div>

                <div className="enquiryInformation--details-more-details-info-workspace">
                  <div className="text-styling-header semi-bold">
                    WORKPLACE LOCATION
                  </div>
                  <div className="text-styling-body">
                    {!isLoading && enquiryDetail ? (
                      splitToNextLine(enquiryDetail.workplaceLocation)
                    ) : (
                      <SkeletonText />
                    )}
                  </div>
                </div>

                <div className="enquiryInformation--details-more-details-info-enquirer">
                  <div className="text-styling-header">ENQUIRER</div>
                  <div className="text-styling-body">
                    {!isLoading && enquiryDetail?.User ? (
                      formatEnquirerDetail(enquiryDetail.User)
                    ) : (
                      <SkeletonText />
                    )}
                  </div>
                </div>

                <div className="enquiryInformation--details-more-details-info-onbehalf">
                  <div className="text-styling-header">ON BEHALF OF</div>
                  <div className="text-styling-body">
                    {!isLoading &&
                    enquiryDetail?.WorkingForAuthority !== undefined ? (
                      enquiryDetail?.WorkingForAuthority?.name ||
                      enquiryDetail?.otherAuthorityName ||
                      'Private'
                    ) : (
                      <SkeletonText />
                    )}
                  </div>
                </div>

                <div className="enquiryInformation--details-more-details-info-notes">
                  <div className="text-styling-header">NOTES</div>
                  <div className="text-styling-body">
                    {!isLoading && enquiryDetail?.notes !== undefined ? (
                      enquiryDetail.notes
                    ) : (
                      <SkeletonText />
                    )}
                  </div>
                </div>

                {screenSize === SCREEN_SIZE.SM &&
                  showMoreDetails &&
                  !showGetPlansButton && (
                    <div className="enquiryInformation--details-more-details-info-less-detail-button">
                      <Button
                        onClick={toggleShowMoreDetailsSection}
                        kind={'ghost'}
                        className={'toggle-more-details'}
                      >
                        Less detail
                      </Button>
                    </div>
                  )}
              </div>
            </div>
            <div
              className={`enquiryInformation--details-collapsed ${
                showDetails ? 'hide' : 'visible'
              }`}
            >
              <div
                className="enquiryInformation--details-collapsed-wrapper"
                onClick={toggleDetailsPanel}
              >
                <Add16 />
                <div>ENQUIRY DETAILS</div>
              </div>
            </div>
          </>
        )}
      </div>
      <Modal
        passiveModal
        onRequestClose={() => {
          handleModalState();
          setMapModalLoaded(false);
        }}
        open={showMapModal}
        className={`map-modal ${
          isModal || isFromSwxLocate ? 'map-modal-popup' : ''
        }`}
      >
        <div className="enquiryInformation--map-modal">
          {showMapModal && (
            <>
              {!mapModalLoaded && (
                <InlineLoading
                  iconDescription="Loading"
                  description="Loading map ..."
                />
              )}
              <MapView
                baseMapTheme={'light'}
                enquiryShape={enquiryDetail?.shape}
                onMapLoaded={() => setMapModalLoaded(true)}
                setLualExtent={setLualExtent}
              />
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default DesktopView;
